import { useCallback } from 'react';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { UserRejectedRequestError, BaseError } from 'viem';
import { useWriteContract, usePublicClient } from 'wagmi';

import { predictionContractAbi } from '@/abi';
import { useRouter } from '@/i18n/navigation';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

interface I_UseClaimRefundsProps {
  isClaiming: boolean;
  setIsClaiming: (value: boolean) => void;
  setClaimingParticipationId: (id: string | null) => void;
}

export function useClaimRefunds({
  isClaiming,
  setIsClaiming,
  setClaimingParticipationId
}: I_UseClaimRefundsProps) {
  const t = useTranslations();
  const router = useRouter();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();

  const handleClaimRefund = useCallback(
    async (
      participation: T_UserBlockchainData['predictionEventParticipations'][0]
    ) => {
      if (
        !participation.predictionEvent.predictionContract.contractAddress ||
        isClaiming
      ) {
        return;
      }

      try {
        setIsClaiming(true);
        setClaimingParticipationId(participation.id);

        const txHash = await writeContractAsync({
          address: participation.predictionEvent.predictionContract
            .contractAddress as `0x${string}`,
          abi: predictionContractAbi,
          functionName: 'claimPendingRefunds',
          args: []
        });

        if (publicClient) {
          await publicClient.waitForTransactionReceipt({
            hash: txHash,
            confirmations: 2
          });
        }

        toast.success(
          t('profile.predictionWinners.claimRefundSuccess') ||
            'Successfully claimed refund!'
        );

        // Refetch pending refunds to update UI (button will be disabled)
        // The component will automatically update when pendingRefunds becomes 0
        // Optionally refresh the page or refetch data
        setTimeout(() => {
          router.refresh();
        }, 1000);
      } catch (error) {
        const isUserRejection =
          error instanceof UserRejectedRequestError ||
          (error instanceof BaseError &&
            String(error.cause).toLowerCase().includes('user rejected'));

        if (!isUserRejection) {
          toast.error(
            t('profile.predictionWinners.claimRefundError') ||
              'Failed to claim refund'
          );
        }
        console.error('Claim refund error:', error);
      } finally {
        setIsClaiming(false);
        setClaimingParticipationId(null);
      }
    },
    [
      isClaiming,
      setIsClaiming,
      setClaimingParticipationId,
      writeContractAsync,
      publicClient,
      router,
      t
    ]
  );

  return { handleClaimRefund };
}
